import { useState } from 'react'
import { ferry, type Me } from '../lib/ferry'

const MUNICIPALITIES = ['Vieques', 'Culebra', 'Ceiba', 'Fajardo', 'Naguabo', 'Humacao', 'Other in Puerto Rico']

/**
 * Prove you live here, and register a vehicle.
 *
 * ── Why this is not automatic ────────────────────────────────────────────────
 *
 * The resident fare is a fraction of the visitor fare, so the incentive to claim
 * it dishonestly is permanent and large. Nothing on this screen approves
 * anything: a submission always lands `pending`, and a person decides. Reading a
 * licence with a camera proves a card exists, not that its holder lives on the
 * island this year.
 *
 * The licence NUMBER is hashed on the server and only its last four digits are
 * kept — enough for a human to check the card in front of them, not enough to be
 * worth stealing.
 */
export default function Residency({ me, onChanged }: { me: Me | null; onChanged: () => void }) {
  const [municipality, setMunicipality] = useState('Vieques')
  const [legalName, setLegalName] = useState('')
  const [licenseNumber, setLicenseNumber] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const status = me?.residency?.status

  async function claim() {
    setBusy(true); setError('')
    try {
      await ferry.claimResidency({ municipality: municipality.toLowerCase(), legalName, licenseNumber, method: 'manual' })
      onChanged()
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  return (
    <section className="card">
      <h2>Residency</h2>

      {status === 'verified' && (
        <p className="notice good">
          Verified — {me?.residency.municipality}. Resident fares apply, you choose
          your own seats, and you can register a vehicle.
        </p>
      )}
      {status === 'pending' && (
        <p className="notice">
          Submitted and waiting on review. You can book at the visitor fare in the
          meantime; there is no need to wait.
        </p>
      )}
      {status === 'rejected' && (
        <p className="notice bad">
          Not verified{me?.residency.reject_reason ? `: ${me.residency.reject_reason}` : '.'} Bring
          your licence to the counter and they can sort it in person.
        </p>
      )}

      {!status && (
        <>
          <p className="muted">
            Puerto Rico residents pay the resident fare. This is checked by a person,
            usually within a day.
          </p>

          {/*
           * The government wallet path is the one worth building toward: a
           * signed credential the holder chooses to share, which proves the
           * licence is real without the ferry ever storing it. It is stubbed
           * here rather than faked, because a button that pretends to talk to a
           * government service is worse than one that says it does not yet.
           */}
          <button className="wallet" disabled title="Coming — needs the DTOP wallet integration">
            Share from the Puerto Rico wallet
          </button>
          <p className="muted small">
            Not connected yet. Enter the details from your licence instead and they
            will be checked by hand.
          </p>

          <label>
            <span>Name as it appears on your licence</span>
            <input value={legalName} onChange={(e) => setLegalName(e.target.value)} autoComplete="name" />
          </label>
          <label>
            <span>Municipality</span>
            <select value={municipality} onChange={(e) => setMunicipality(e.target.value)}>
              {MUNICIPALITIES.map((m) => <option key={m}>{m}</option>)}
            </select>
          </label>
          <label>
            <span>Licence number</span>
            <input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} autoComplete="off" spellCheck={false} />
          </label>
          <p className="muted small">
            Only the last four digits are kept. The rest is hashed and cannot be read back.
          </p>

          {error && <p className="error">{error}</p>}
          <button className="primary" disabled={busy || !legalName.trim() || !licenseNumber.trim()} onClick={() => void claim()}>
            {busy ? 'Sending…' : 'Submit for review'}
          </button>
        </>
      )}

      {status === 'verified' && <Vehicles me={me} onChanged={onChanged} />}
    </section>
  )
}

function Vehicles({ me, onChanged }: { me: Me | null; onChanged: () => void }) {
  const [plate, setPlate] = useState('')
  const [make, setMake] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [uploadId, setUploadId] = useState('')

  async function upload(file: File) {
    setError('')
    const form = new FormData()
    form.append('file', file)
    const res = await fetch('/api/uploads', { method: 'POST', body: form, credentials: 'same-origin' })
    if (!res.ok) { setError('That file could not be uploaded.'); return }
    setUploadId(((await res.json()) as { id: string }).id)
  }

  async function add() {
    setBusy(true); setError('')
    try {
      await ferry.addVehicle({ plate, make, ownershipUploadId: uploadId })
      setPlate(''); setMake(''); setUploadId('')
      onChanged()
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  return (
    <>
      <h3>Vehicles</h3>
      {(me?.vehicles ?? []).length === 0 && <p className="muted">No vehicles registered.</p>}
      <ul className="party">
        {(me?.vehicles ?? []).map((v) => (
          <li key={v.id}>
            <span>{v.plate}{v.make ? ` · ${v.make}` : ''}</span>
            <span className={`badge badge-${v.status}`}>{v.status}</span>
          </li>
        ))}
      </ul>

      <details>
        <summary>Register a vehicle</summary>
        <p className="muted small">
          {/*
           * The document requirement is not bureaucracy for its own sake: a ferry
           * is a route for moving a car off an island, and "I am taking my car
           * home" and "I am taking someone else's car away" look identical at the
           * ramp.
           */}
          Taking a car on or off Vieques needs proof that it is yours — a registration
          or title. It is checked by a person before the vehicle can be booked.
        </p>
        <label>
          <span>Plate</span>
          <input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} spellCheck={false} />
        </label>
        <label>
          <span>Make and model</span>
          <input value={make} onChange={(e) => setMake(e.target.value)} />
        </label>
        <label>
          <span>Registration or title</span>
          <input type="file" accept="image/*,application/pdf" capture="environment"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f) }} />
        </label>
        {uploadId && <p className="muted small">Document attached.</p>}
        {error && <p className="error">{error}</p>}
        <button className="primary" disabled={busy || !plate.trim() || !uploadId} onClick={() => void add()}>
          {busy ? 'Saving…' : 'Register vehicle'}
        </button>
      </details>
    </>
  )
}
